"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, Copy } from "lucide-react";
import { formatCurrency, formatDate, copyToClipboard } from "@/lib/utils";
import { format } from "date-fns";
import { PaymentOutput } from "@/types/payment.types";
import { Customer, Vehicle, Appointment } from "@/types";
import { useState, useEffect } from "react";
import { toast } from "sonner";

interface PaymentDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: PaymentOutput | null;
  customers: Customer[];
  vehicles: Vehicle[];
  appointments?: Appointment[];
  onUpdatePaymentStatus: (
    payment: PaymentOutput,
    newStatus: string,
  ) => Promise<void>;
  onUpdatePaymentAmount?: (
    payment: PaymentOutput,
    newAmount: number,
  ) => Promise<void>;
  onUpdateDiscount?: (
    payment: PaymentOutput,
    newDiscount: number,
  ) => Promise<void>;
  onPartialPayment?: (payment: PaymentOutput, amount: number) => Promise<void>;
  onDownloadReceipt: (payment: PaymentOutput) => void;
}

export function PaymentDetailDialog({
  open,
  onOpenChange,
  payment,
  customers,
  vehicles,
  appointments = [],
  onUpdatePaymentStatus,
  onUpdatePaymentAmount,
  onUpdateDiscount,
  onPartialPayment,
  onDownloadReceipt,
}: PaymentDetailDialogProps) {
  const [amount, setAmount] = useState<string>("");
  const [discount, setDiscount] = useState<string>("");
  const [discountError, setDiscountError] = useState<string>("");
  const [partialPaymentAmount, setPartialPaymentAmount] = useState<string>("");
  const [partialPaymentError, setPartialPaymentError] = useState<string>("");
  const [isSubmittingPartial, setIsSubmittingPartial] =
    useState<boolean>(false);

  useEffect(() => {
    if (payment) {
      setAmount(payment.totalAmount.toString());
      setDiscount((payment.discount ?? 0).toString());
      setDiscountError("");
      setPartialPaymentAmount("");
      setPartialPaymentError("");
    }
  }, [payment]);

  if (!payment) return null;

  const getPaymentStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: "bg-yellow-500 hover:bg-yellow-600",
      COMPLETED: "bg-green-500 hover:bg-green-600",
      CANCELLED: "bg-red-500 hover:bg-red-600",
    };

    return (
      <Badge className={colors[status] || "bg-gray-500 hover:bg-gray-600"}>
        {status}
      </Badge>
    );
  };

  const customer = payment.customer?.id
    ? customers.find((c) => c.id === payment.customer?.id) || null
    : null;

  const vehicle = payment.vehicle?.id
    ? vehicles.find((v) => v.id === payment.vehicle?.id) || null
    : null;

  // Resolve multiple vehicles if payment is linked to an appointment
  const paymentVehicles: Vehicle[] = [];

  // 1. Try to find linked appointment
  const linkedAppointment =
    payment.refType === "APPOINTMENT" &&
    payment.refId &&
    appointments.length > 0
      ? appointments.find((a) => a.id === payment.refId)
      : null;

  // 2. If valid appointment with vehicles, use those
  if (
    linkedAppointment &&
    linkedAppointment.vehicleIds &&
    linkedAppointment.vehicleIds.length > 0
  ) {
    linkedAppointment.vehicleIds.forEach((vId) => {
      const v = vehicles.find((veh) => veh.id === vId);
      if (v) paymentVehicles.push(v);
    });
  }
  // 3. Fallback to single vehicle from payment object
  else if (vehicle) {
    paymentVehicles.push(vehicle);
  } else if (payment.vehicle) {
    // Last resort: use the incomplete vehicle object embedded in payment if ID lookup failed
    // Need to cast to compatible type or use as is
    paymentVehicles.push({
      ...payment.vehicle,
      // Add required fields that might be missing in reduced payment.vehicle object
      customerId: payment.customer?.id || "",
      totalServices: 0,
      type: "sedan", // Default fallback
    } as Vehicle);
  }

  const handleStatusChange = async (newStatus: string) => {
    await onUpdatePaymentStatus(payment, newStatus);
  };

  const handleAmountChange = async (newAmount: string) => {
    const numAmount = parseFloat(newAmount);
    if (!isNaN(numAmount) && numAmount >= 0 && onUpdatePaymentAmount) {
      await onUpdatePaymentAmount(payment, numAmount);
    }
  };

  const handleDiscountChange = async (newDiscount: string) => {
    const numDiscount = parseFloat(newDiscount);

    // Validation
    if (isNaN(numDiscount) || numDiscount < 0) {
      setDiscountError("Discount must be a non-negative number");
      return;
    }

    // Maximum manual discount is totalAmount minus platinumDiscount
    const maxManualDiscount = Math.max(0, payment.totalAmount - (payment.platinumDiscount || 0));
    
    if (numDiscount > maxManualDiscount) {
      setDiscountError(
        `Discount cannot exceed ${formatCurrency(maxManualDiscount)}${payment.platinumDiscount > 0 ? " (after platinum discount)" : ""}`,
      );
      return;
    }

    setDiscountError("");

    if (onUpdateDiscount) {
      await onUpdateDiscount(payment, numDiscount);
    }
  };

  const handleShare = async () => {
    try {
      const receiptUrl = `${window.location.origin}/receipt/${payment.id}`;
      await copyToClipboard(receiptUrl);
      toast.success("Receipt link copied to clipboard!");
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast.error("Failed to copy link to clipboard");
    }
  };

  const handlePartialPayment = async () => {
    if (!payment || !onPartialPayment) return;

    setPartialPaymentError("");
    const numAmount = parseFloat(partialPaymentAmount);

    // Validation
    if (isNaN(numAmount) || numAmount <= 0) {
      setPartialPaymentError("Amount must be greater than 0");
      return;
    }

    if (numAmount > payment.dueAmount) {
      setPartialPaymentError(
        `Amount cannot exceed due amount of ${formatCurrency(payment.dueAmount)}`,
      );
      return;
    }

    setIsSubmittingPartial(true);
    try {
      // Call the callback - parent will handle the API call
      await onPartialPayment(payment, numAmount);

      toast.success(
        `Partial payment of ${formatCurrency(numAmount)} recorded successfully`,
      );
      setPartialPaymentAmount("");
      setPartialPaymentError("");
    } catch (error: any) {
      console.error("Error recording partial payment:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to record partial payment";
      toast.error(errorMessage);
      setPartialPaymentError(errorMessage);
    } finally {
      setIsSubmittingPartial(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl overflow-y-auto max-h-[calc(100vh-10rem)]">
        <DialogHeader>
          <DialogTitle>Payment Details</DialogTitle>
          <DialogDescription>Payment ID: {payment.id}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Payment Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4">
            <div>
              <Label className="text-muted-foreground">Payment ID</Label>
              <p className="mt-1 font-mono text-sm">{payment.id}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Payment Date</Label>
              <p className="mt-1">
                {payment.date ? formatDate(new Date(payment.date)) : "N/A"}
              </p>
            </div>
            {payment.refType === "APPOINTMENT" && payment.appointmentScheduledAt && (
              <div>
                <Label className="text-muted-foreground">Scheduled Date & Time</Label>
                <p className="mt-1">
                  {format(new Date(payment.appointmentScheduledAt), "MMM dd, yyyy HH:mm")}
                </p>
              </div>
            )}
            <div>
              <Label className="text-muted-foreground">Total Amount</Label>
              <p className="mt-1 text-lg font-bold">
                {formatCurrency(payment.totalAmount)}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">Paid Amount</Label>
              <p className="mt-1 text-lg font-semibold text-green-600">
                {formatCurrency(payment.paidAmount)}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">Discount</Label>
              <p className="mt-1 text-lg font-semibold text-blue-600">
                {formatCurrency(payment.discount ?? 0)}
              </p>
            </div>
            {payment.platinumDiscount > 0 && (
              <div>
                <Label className="text-muted-foreground">
                  Platinum Member Discount (15%)
                </Label>
                <p className="mt-1 text-lg font-semibold text-amber-600">
                  {formatCurrency(payment.platinumDiscount)}
                </p>
              </div>
            )}
            <div>
              <Label className="text-muted-foreground">Due Amount</Label>
              <p className="mt-1 text-lg font-semibold text-orange-600">
                {formatCurrency(payment.dueAmount)}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">Payment Status</Label>
              <div className="mt-1">
                {getPaymentStatusBadge(payment.status)}
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">Payment Mode</Label>
              <p className="mt-1 capitalize">{payment.mode}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Reference Type</Label>
              <p className="mt-1">
                <Badge variant="outline">{payment.refType}</Badge>
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">Reference ID</Label>
              <p className="mt-1 font-mono text-sm">{payment.refId}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Service Type</Label>
              <p className="mt-1">
                {payment.serviceType ? (
                  <Badge variant="outline" className="capitalize">
                    {payment.serviceType === "one-time"
                      ? "One-Time"
                      : "Subscription"}
                  </Badge>
                ) : (
                  "N/A"
                )}
              </p>
            </div>
          </div>

          {/* Customer Info */}
          <div>
            <Label className="text-muted-foreground">Customer:</Label>
            <div className="mt-2">
              <p className="font-medium">
                {customer?.name || payment.customer?.name || "N/A"}
              </p>
              {customer && (
                <>
                  <p className="text-sm text-muted-foreground">
                    {customer.phone || ""}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {customer.email || ""}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Vehicle Info */}
          <div>
            <Label className="text-muted-foreground">
              Vehicle{paymentVehicles.length > 1 ? "s" : ""}:
            </Label>
            <div className="mt-1 space-y-1">
              {paymentVehicles.length > 0 ? (
                paymentVehicles.map((v) => (
                  <p key={v.id || Math.random().toString()}>
                    {v.make} {v.model}{" "}
                    {v.plateNumber ? `- ${v.plateNumber}` : ""}
                  </p>
                ))
              ) : (
                <p>N/A</p>
              )}
            </div>
          </div>

          {/* Status Update */}
          <div className="border-t pt-4">
            <Label className="text-muted-foreground mb-2 block">
              Update Payment Status:
            </Label>
            <Select value={payment.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Amount Update */}
          {onUpdatePaymentAmount && (
            <div className="border-t pt-4">
              <Label className="text-muted-foreground mb-2 block">
                Update Payment Amount:
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={(e) => {
                  const newAmount = e.target.value;
                  if (newAmount !== payment.totalAmount.toString()) {
                    handleAmountChange(newAmount);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
                className="w-full"
                placeholder="Enter amount"
              />
            </div>
          )}

          {/* Discount Update */}
          {onUpdateDiscount && (
            <div className="border-t pt-4">
              <Label className="text-muted-foreground mb-2 block">
                Update Discount:
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={payment.totalAmount}
                value={discount}
                onChange={(e) => {
                  setDiscount(e.target.value);
                  setDiscountError("");
                }}
                onBlur={(e) => {
                  const newDiscount = e.target.value;
                  if (newDiscount !== (payment.discount ?? 0).toString()) {
                    handleDiscountChange(newDiscount);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
                className="w-full"
                placeholder="Enter discount amount"
              />
              {discountError && (
                <p className="text-sm text-red-600 mt-1">{discountError}</p>
              )}
              <p className="text-sm text-muted-foreground mt-1">
                Maximum discount: {formatCurrency(payment.totalAmount)}
              </p>
            </div>
          )}

          {/* Partial Payment */}
          {payment.dueAmount > 0 && (
            <div className="border-t pt-4">
              <Label className="text-muted-foreground mb-2 block">
                Record Partial Payment:
              </Label>
              <div className="space-y-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={payment.dueAmount}
                  value={partialPaymentAmount}
                  onChange={(e) => {
                    setPartialPaymentAmount(e.target.value);
                    setPartialPaymentError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handlePartialPayment();
                    }
                  }}
                  className="w-full"
                  placeholder={`Enter amount (max: ${formatCurrency(payment.dueAmount)})`}
                  disabled={isSubmittingPartial}
                />
                {partialPaymentError && (
                  <p className="text-sm text-red-600">{partialPaymentError}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  Maximum amount: {formatCurrency(payment.dueAmount)}
                </p>
                <Button
                  onClick={handlePartialPayment}
                  disabled={
                    isSubmittingPartial ||
                    !partialPaymentAmount ||
                    parseFloat(partialPaymentAmount) <= 0
                  }
                  className="w-full"
                >
                  {isSubmittingPartial ? "Recording..." : "Record Payment"}
                </Button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={handleShare}>
              <Copy className="mr-2 h-4 w-4" />
              Copy Link
            </Button>
            <Button
              variant="outline"
              onClick={() => onDownloadReceipt(payment)}
            >
              <Download className="mr-2 h-4 w-4" />
              Download Receipt
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
